import {
    addDays,
    addMonths,
    addWeeks,
    format,
    isValid,
    parseISO,
    startOfMonth,
    startOfWeek
} from 'date-fns'
import type {
    CustomerCalendarEntry,
    CustomerCalendarView
} from '../../../services/customer-calendar.service'
import {calendarDateTimeForLocale} from '@fitro/date-time/display'

const DATE_KEY_FORMAT = 'yyyy-MM-dd'

export const dateKey = (date: Date) => format(date, DATE_KEY_FORMAT)

export const parseDateKey = (value?: string | null): Date | null => {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null
    const parsed = parseISO(value)
    return isValid(parsed) && dateKey(parsed) === value ? parsed : null
}

export const resolveCalendarRange = (
    view: CustomerCalendarView,
    anchor: Date
): {from: Date; to: Date; days: Date[]} => {
    if (view === 'day') {
        const from = parseDateKey(dateKey(anchor)) ?? anchor
        return {from, to: addDays(from, 1), days: [from]}
    }
    if (view === 'week') {
        const from = startOfWeek(anchor, {weekStartsOn: 1})
        return {
            from,
            to: addDays(from, 7),
            days: Array.from({length: 7}, (_, index) => addDays(from, index))
        }
    }
    const from = startOfWeek(startOfMonth(anchor), {weekStartsOn: 1})
    return {
        from,
        to: addDays(from, 42),
        days: Array.from({length: 42}, (_, index) => addDays(from, index))
    }
}

export const moveCalendarAnchor = (
    view: CustomerCalendarView,
    anchor: Date,
    direction: -1 | 1
) => {
    if (view === 'day') return addDays(anchor, direction)
    if (view === 'week') return addWeeks(anchor, direction)
    return addMonths(anchor, direction)
}

export interface CustomerCalendarDayTimeRange {
    date: string
    startTime: string
    endTime: string
}

export interface CustomerCalendarDayEntry {
    entry: CustomerCalendarEntry
    timeRange: CustomerCalendarDayTimeRange
}

export const compareCustomerCalendarDayEntries = (
    left: CustomerCalendarDayEntry,
    right: CustomerCalendarDayEntry
) => left.timeRange.startTime.localeCompare(right.timeRange.startTime)

export const entryTimeRangeOnDate = (
    entry: Pick<
        CustomerCalendarEntry,
        'localStartDate' | 'localEndDate' | 'localStartTime' | 'localEndTime'
    >,
    day: Date
): CustomerCalendarDayTimeRange | null => {
    const key = dateKey(day)
    if (key < entry.localStartDate || key > entry.localEndDate) return null

    const startTime = key === entry.localStartDate ? entry.localStartTime : '00:00:00'
    const endTime = key === entry.localEndDate ? entry.localEndTime : '24:00:00'
    return endTime > startTime ? {date: key, startTime, endTime} : null
}

export const formatCalendarTitle = (
    view: CustomerCalendarView,
    anchor: Date,
    from: Date,
    to: Date,
    locale: string
) => {
    const dateTime = calendarDateTimeForLocale(locale)
    if (view === 'day') {
        return dateTime.formatLocalDateWithWeekday(dateKey(anchor), {
            dateStyle: 'long',
            weekday: 'long',
            context: 'sentence',
            includeYear: false,
        })
    }
    if (view === 'month') {
        return dateTime.formatLocalDateMonthTitle(dateKey(anchor))
    }
    const inclusiveTo = addDays(to, -1)
    return dateTime.formatLocalDateRange(dateKey(from), dateKey(inclusiveTo))
}
